import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PageLoader } from './ui';
import { useAuth } from '../context/AuthContext';
import { P, roleLabels } from '../lib/permissions';
import { initials } from '../lib/format';

/* ------------------------------------------------------------------ *
 *  Panel kabugu: yan menu, ust cubuk ve yetki kontrolu.
 *
 *  Menu ogeleri kullanicinin izinlerine gore filtrelenir. Yetkisi
 *  olmayan bir yolu elle yazan kullanici sunucudan 403 alir; arayuz
 *  gizlemesi yalnizca kolaylik icindir.
 * ------------------------------------------------------------------ */

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  permissions?: string[];
  end?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

function Icon({ path }: { path: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px] shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

const NAV: NavGroup[] = [
  {
    title: 'Genel',
    items: [
      {
        to: '/',
        label: 'Pano',
        end: true,
        icon: <Icon path="M4 13h6V4H4v9zm0 7h6v-5H4v5zm10 0h6v-9h-6v9zm0-16v5h6V4h-6z" />,
        permissions: [P.reports.view],
      },
      {
        to: '/raporlar',
        label: 'Raporlar',
        icon: <Icon path="M3 3v18h18M7 16l4-4 4 4 5-6" />,
        permissions: [P.reports.view],
      },
    ],
  },
  {
    title: 'Satış',
    items: [
      {
        to: '/siparisler',
        label: 'Siparişler',
        icon: <Icon path="M6 4h12l1.5 15a2 2 0 0 1-2 2.2H6.5A2 2 0 0 1 4.5 19L6 4zm3 4a3 3 0 0 0 6 0" />,
        permissions: [P.orders.view],
      },
      {
        to: '/sevkiyat',
        label: 'Sevkiyat',
        icon: <Icon path="M3 7h11v10H3V7zm11 3h4l3 3v4h-7v-7zM7 20a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm11 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />,
        permissions: [P.orders.fulfill],
      },
      {
        to: '/iadeler',
        label: 'İadeler',
        icon: <Icon path="M9 14l-4-4 4-4M5 10h9a5 5 0 0 1 0 10h-3" />,
        permissions: [P.orders.returns],
      },
      {
        to: '/musteriler',
        label: 'Müşteriler',
        icon: <Icon path="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm-8 8a8 8 0 0 1 16 0" />,
        permissions: [P.customers.view],
      },
    ],
  },
  {
    title: 'Katalog',
    items: [
      {
        to: '/urunler',
        label: 'Ürünler',
        icon: <Icon path="M4 7l8-4 8 4v10l-8 4-8-4V7zm8 4l8-4M12 11v10M12 11L4 7" />,
        permissions: [P.products.view],
      },
      {
        to: '/kategoriler',
        label: 'Kategoriler',
        icon: <Icon path="M4 6h16M4 12h16M4 18h10" />,
        permissions: [P.products.view],
      },
      {
        to: '/markalar',
        label: 'Markalar',
        icon: <Icon path="M20.5 11.5L12.5 3.5H4v8.5l8 8 8.5-8.5zM7.5 8h.01" />,
        permissions: [P.products.view],
      },
      {
        to: '/koleksiyonlar',
        label: 'Koleksiyonlar',
        icon: <Icon path="M4 5h16v6H4V5zm0 8h7v6H4v-6zm9 0h7v6h-7v-6z" />,
        permissions: [P.products.view],
      },
      {
        to: '/yorumlar',
        label: 'Yorumlar',
        icon: <Icon path="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12z" />,
        permissions: [P.products.update, P.content.manage],
      },
      {
        to: '/stok',
        label: 'Stok',
        icon: <Icon path="M4 8l8-4 8 4v8l-8 4-8-4V8zm0 0l8 4m0 0l8-4m-8 4v8" />,
        permissions: [P.inventory.view],
      },
    ],
  },
  {
    title: 'Pazarlama',
    items: [
      {
        to: '/indirimler',
        label: 'İndirimler',
        icon: <Icon path="M9 15l6-6M9.5 9.5h.01M14.5 14.5h.01M12 3l2.4 2.4 3.4-.4.4 3.4L21 12l-2.4 2.4.4 3.4-3.4.4L12 21l-2.4-2.4-3.4.4-.4-3.4L3 12l2.4-2.4-.4-3.4 3.4-.4L12 3z" />,
        permissions: [P.discounts.view],
      },
      {
        to: '/hediye-kartlari',
        label: 'Hediye kartları',
        icon: <Icon path="M3 8h18v12H3V8zm0 4h18M12 8V6a2.5 2.5 0 1 0-2.5 2.5H12zm0 0h2.5A2.5 2.5 0 1 0 12 6v2z" />,
        permissions: [P.discounts.manage],
      },
      {
        to: '/icerik',
        label: 'İçerik',
        icon: <Icon path="M6 3h9l4 4v14H6V3zm9 0v4h4M9 12h7M9 16h7" />,
        permissions: [P.content.view],
      },
      {
        to: '/eposta-sablonlari',
        label: 'E-Posta Şablonları',
        icon: <Icon path="M3 5h18v14H3V5zm0 2l9 6 9-6" />,
        permissions: [P.settings.view],
      },
    ],
  },
  {
    title: 'Yapılandırma',
    items: [
      {
        to: '/kargo',
        label: 'Kargo',
        icon: <Icon path="M3 7h11v10H3V7zm11 3h4l3 3v4h-7v-7z" />,
        permissions: [P.shipping.view],
      },
      {
        to: '/vergi',
        label: 'Vergi',
        icon: <Icon path="M8 8h8M8 12h8M8 16h5M5 3h14v18H5V3z" />,
        permissions: [P.settings.view],
      },
      {
        to: '/toplu-islemler',
        label: 'Toplu işlemler',
        icon: <Icon path="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />,
        permissions: [P.products.bulk],
      },
      {
        to: '/webhooks',
        label: 'Webhook',
        icon: <Icon path="M9 12a3 3 0 1 1 6 0M6.5 9.5A6 6 0 0 0 9 20h6a6 6 0 0 0 2.5-11.5" />,
        permissions: [P.settings.manage],
      },
      {
        to: '/personel',
        label: 'Personel',
        icon: <Icon path="M16 20v-1a4 4 0 0 0-8 0v1M12 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM19 8v6M22 11h-6" />,
        permissions: [P.security.usersManage],
      },
      {
        to: '/gunlukler',
        label: 'Günlükler',
        icon: <Icon path="M5 4h14v16H5V4zm3 4h8M8 12h8M8 16h5" />,
        permissions: [P.security.logsView, P.security.auditView],
      },
      {
        to: '/ayarlar',
        label: 'Ayarlar',
        icon: <Icon path="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm7.4-3a7.4 7.4 0 0 0-.1-1.2l2-1.5-2-3.4-2.3 1a7.5 7.5 0 0 0-2-1.2l-.3-2.4h-4l-.4 2.4a7.5 7.5 0 0 0-2 1.2l-2.3-1-2 3.4 2 1.5a7.4 7.4 0 0 0 0 2.4l-2 1.5 2 3.4 2.3-1a7.5 7.5 0 0 0 2 1.2l.4 2.4h4l.3-2.4a7.5 7.5 0 0 0 2-1.2l2.3 1 2-3.4-2-1.5c.1-.4.1-.8.1-1.2z" />,
        permissions: [P.settings.view],
      },
      {
        to: '/hesabim',
        label: 'Hesabım & Güvenlik',
        icon: <Icon path="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
      },
    ],
  },
];

export function Shell() {
  const { booting, isAuthenticated, isStaff, user, canAny, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!booting && !isAuthenticated) {
      navigate('/giris', { replace: true });
    }
  }, [booting, isAuthenticated, navigate]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (booting) return <PageLoader label="Oturum kontrol ediliyor…" />;
  if (!isAuthenticated) return null;

  if (!isStaff) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-ink-900">Bu panele erişiminiz yok</h1>
          <p className="mt-2 text-sm text-ink-600">
            Hesabınız yönetim rollerinden birine sahip değil. Erişim gerekiyorsa yöneticinize
            başvurun.
          </p>
          <button type="button" className="btn-secondary mt-5" onClick={() => void logout()}>
            Çıkış yap
          </button>
        </div>
      </div>
    );
  }

  const groups = NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.permissions || canAny(...item.permissions)),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="flex min-h-screen bg-ink-50">
      {/* Yan menu */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-60 shrink-0 overflow-y-auto border-r border-ink-200 bg-white transition-transform lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-14 items-center gap-2 border-b border-ink-200 px-5">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-700 text-sm text-amber-300">
            🌴
          </span>
          <span className="text-sm font-bold tracking-tight text-ink-900">
            Hurmahane Yönetim
          </span>
        </div>

        <nav className="space-y-6 px-3 py-4" aria-label="Panel menüsü">
          {groups.map((group) => (
            <div key={group.title}>
              <p className="mb-1.5 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                {group.title}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition ${
                          isActive
                            ? 'bg-ink-900 font-medium text-white'
                            : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900'
                        }`
                      }
                    >
                      {item.icon}
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {sidebarOpen ? (
        <div
          className="fixed inset-0 z-30 bg-ink-950/30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      ) : null}

      {/* Icerik */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-ink-200 bg-white px-4 lg:px-6">
          <button
            type="button"
            className="-ml-1 rounded-lg p-2 text-ink-600 lg:hidden"
            onClick={() => setSidebarOpen((open) => !open)}
            aria-label="Menüyü aç"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75">
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
            </svg>
          </button>

          <div className="ml-auto flex items-center gap-3">
            <NavLink
              to="/hesabim"
              className="flex items-center gap-2.5 rounded-lg px-2 py-1 transition hover:bg-ink-100"
              title="Hesabım ve Güvenlik"
            >
              <div className="hidden text-right sm:block">
                <p className="text-sm font-medium text-ink-900">{user?.fullName ?? user?.email}</p>
                <p className="text-xs text-ink-500">
                  {(user?.roles ?? []).map((role) => roleLabels[role] ?? role).join(', ')}
                </p>
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-ink-900 text-xs font-semibold text-white">
                {initials(user?.fullName ?? user?.email)}
              </div>
            </NavLink>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => void logout()}
            >
              Çıkış
            </button>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Yetkisi olmayan sayfalar icin kibar bir engel. */
export function RequirePermission({
  permissions,
  children,
}: {
  permissions: string[];
  children: ReactNode;
}) {
  const { canAny } = useAuth();

  if (!canAny(...permissions)) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-6 py-10 text-center">
        <h2 className="text-base font-semibold text-amber-900">Bu bölüme yetkiniz yok</h2>
        <p className="mt-1.5 text-sm text-amber-800">
          Gerekli izin: {permissions.join(' veya ')}
        </p>
      </div>
    );
  }

  return <>{children}</>;
}

/** Sayfa basligi ve eylem alani. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-ink-900">{title}</h1>
        {description ? <p className="mt-1 text-sm text-ink-500">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}
